import { useEffect, useState } from "react";
import { Form } from "@/components/shared/forms/Form";
import { useForm, SubmitHandler } from "react-hook-form";
import { useRouteContext } from "@tanstack/react-router";

import { SLNGAddEditForm } from "@/components/records/child/SLNGAddEditForm";
import Popup from "@/components/shared/Popup";
import LoaderButton from "@/components/shared/LoaderButton";
import { DeleteConfirmModal } from "@/components/shared/DeleteConfirmModal";
import { ConfirmModal } from "@/components/shared/ConfirmModal";
import { focusFirstInvalidField } from "@/utils/forms";
import { useRecordSubmission } from "@/hooks/useRecordSubmission";
import { useLeaData } from "@/queries.ts";
import { SLNG_TEMPLATE, type SLNG } from "@/models";
import { slngMutationFunctions } from "@/models/chld";
import {
  FiArrowLeft,
  FiEdit,
  FiEye,
  FiPlus,
  FiSave,
  FiTrash2,
} from "react-icons/fi";

export type SLNGEditDialogProps = {
  selectedSLNG: SLNG | undefined;
  slngDefaults?: Partial<SLNG>;
  onClose: () => void;
  viewOnly?: boolean;
  onDeleted?: (record: SLNG) => void;
  footerMode?: "default" | "back";
};

export default function SLNGEditDialog({
  selectedSLNG,
  slngDefaults,
  onClose,
  viewOnly = false,
  onDeleted,
  footerMode = "default",
}: SLNGEditDialogProps) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const leaData = useLeaData(leaContext.currentUserOrg.esiIdPath).data;

  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [submitConfirm, setSubmitConfirm] = useState<{
    data: SLNG;
    dateChanged: boolean;
  } | null>(null);
  const [isOpen] = useState(true);

  const initialValues: SLNG =
    selectedSLNG ?? ({ ...SLNG_TEMPLATE, ...(slngDefaults ?? {}) } as SLNG);

  const addEditForm = useForm<SLNG>({ defaultValues: initialValues });

  useEffect(() => {
    if (selectedSLNG) {
      addEditForm.reset(selectedSLNG);
    } else {
      addEditForm.reset({ ...SLNG_TEMPLATE, ...(slngDefaults ?? {}) });
    }
    addEditForm.clearErrors();
  }, [selectedSLNG, slngDefaults, addEditForm]);

  const { isSubmitting, setSubmitResult, handleSubmitStart } =
    useRecordSubmission({
      form: addEditForm,
      recordType: "SLNG",
      toastMessage:
        "Supplemental language record submitted.\nWaiting for validation...",
      onSuccess: onClose,
    });

  const {
    isSubmitting: isDeleteSubmitting,
    setSubmitResult: setDeleteSubmitResult,
    handleSubmitStart: handleDeleteStart,
  } = useRecordSubmission({
    form: addEditForm,
    recordType: "SLNG",
    toastMessage: "Supplemental language record submitted for deletion",
    successMessage: "Supplemental language record deleted successfully",
    onSuccess: () => {
      if (selectedSLNG) onDeleted?.(selectedSLNG);
      onClose();
    },
  });

  const mutateFns = slngMutationFunctions(leaData, setSubmitResult);
  const deleteMutateFns = slngMutationFunctions(leaData, setDeleteSubmitResult);

  const onSubmit: SubmitHandler<SLNG> = (x) => {
    if (!selectedSLNG) {
      handleSubmitStart();
      mutateFns.handleSave(x);
      return;
    }
    const dateChanged =
      (selectedSLNG.EffectiveStartDate ?? "") !== (x.EffectiveStartDate ?? "");
    setSubmitConfirm({ data: x, dateChanged });
  };

  const handleConfirmSubmit = () => {
    if (!submitConfirm) return;
    const data = submitConfirm.data;
    setSubmitConfirm(null);
    handleSubmitStart();
    mutateFns.handleSave(data);
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    if (selectedSLNG) {
      setIsDeleting(true);
      handleDeleteStart();
      deleteMutateFns.handleSave(selectedSLNG, true);
    }
  };

  const formId = "frmSLNG";
  const hasRole = leaContext.hasRole("SLNGEdit");
  const canEdit = hasRole && !viewOnly;
  const isBack = footerMode === "back";

  const deleteButton =
    hasRole && selectedSLNG ? (
      <LoaderButton
        variant="danger"
        type="button"
        onClick={() => setShowDeleteConfirm(true)}
        loading={isDeleting && isDeleteSubmitting}
        loadingText="Deleting..."
        className="d-flex align-items-center"
      >
        <FiTrash2 className="me-1" />
        Delete
      </LoaderButton>
    ) : undefined;

  return (
    <>
      <Popup
        show={true}
        enforceFocus={!showDeleteConfirm && !submitConfirm}
        header={
          selectedSLNG == null ? (
            <div
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "1.25rem" }}
            >
              {canEdit ? <FiPlus size={20} /> : <FiEye size={20} />}
              <span>
                {canEdit
                  ? "Add Supplemental Language Record"
                  : "View Supplemental Language Record"}
              </span>
            </div>
          ) : (
            <div
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "1.25rem" }}
            >
              {canEdit ? <FiEdit size={20} /> : <FiEye size={20} />}
              <span>
                {canEdit
                  ? "Edit Supplemental Language Record"
                  : "View Supplemental Language Record"}
              </span>
            </div>
          )
        }
        hasButton={false}
        submitFormId={canEdit ? formId : undefined}
        submitButtonText={
          selectedSLNG ? (
            <span className="d-flex align-items-center">
              <FiEdit className="me-1" />
              Update
            </span>
          ) : (
            <span className="d-flex align-items-center">
              <FiSave className="me-1" />
              Save
            </span>
          )
        }
        submitButtonLoading={isSubmitting}
        submitButtonLoadingText="Submitting..."
        secondButtonText={isBack ? undefined : "Cancel"}
        size="lg"
        onClose={onClose}
        onSecondButtonClick={isBack ? undefined : onClose}
        backButtonText={
          isBack ? (
            <div className="d-flex align-items-center gap-2">
              <FiArrowLeft size={16} />
              <span>Back</span>
            </div>
          ) : undefined
        }
        onBackButtonClick={isBack ? onClose : undefined}
        canEdit={canEdit}
        additionalFooterButton={isBack ? deleteButton : undefined}
        leftFooterButton={isBack ? undefined : deleteButton}
      >
        <Form
          id={formId}
          onSubmit={addEditForm.handleSubmit(onSubmit, () =>
            focusFirstInvalidField(`#${formId}`)
          )}
        >
          <SLNGAddEditForm
            selectedSLNG={selectedSLNG}
            form={addEditForm}
            isOpen={isOpen}
          />
        </Form>
      </Popup>

      {showDeleteConfirm && (
        <DeleteConfirmModal
          recordKindLabel="Supplemental language record"
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={handleConfirmDelete}
        />
      )}

      {submitConfirm && (
        <ConfirmModal
          title={
            submitConfirm.dateChanged
              ? "Confirm Effective Date Change"
              : "Confirm Record Update"
          }
          message={
            submitConfirm.dateChanged
              ? "The changes requested have a different effective date. They will insert a new record with these updates and automatically expire the old record."
              : "The changes requested will overwrite the existing record. If these changes became effective at a later date than when the record was originally input, please input the associated effective date."
          }
          confirmText="Submit"
          onCancel={() => setSubmitConfirm(null)}
          onConfirm={handleConfirmSubmit}
        />
      )}
    </>
  );
}
